import {
  ArrowsPointingOutIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { AnimatePresence, motion } from 'motion/react';
import { Fragment, PropsWithChildren, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';

import { onEditBookFn } from '@/components/admin/lessons/material/Material.hooks';
import PedWorkspaceGuard from '@/components/shared/PedWorkspaceGuard';
import ModalDisable from '@/components/ui/custom/modals/ModalDisable';
import useCourseEditing from '@/data/hook/useCourseEditing';
import useToggle from '@/data/hook/useToggle';
import Book from '@/models/Book';
import Chapter from '@/models/Chapter';
import Subchapter from '@/models/Subchapter';
import { fadeIn } from '@/utils/animations/commom';
import { scrollBarClassName } from '@/utils/scrollBarClassName';
import MainButton from '../../../buttons/MainButton';
import ConditionalRenderer from '../../../ConditionalRenderer';
import Text from '../../../dataDisplay/Text';
import ChapterService from '../chapters/ChapterService';
import Instructions from '../instructions/Instructions';
import ChapterView from '../subchapters/SubchapterView';
import { useChapter, useSubchapter } from './BookForm.hooks';
import Chapters from './Chapters';
import { usePedWorkspaceStatus } from '@/data/context/PedWorkspaceContext';

export type BookFormFields = Pick<Book, 'filename'>;

type BookFormProps = {
  book: Book;
  lessonId: number;
  readOnly?: boolean;
  onUpdateChapter: (chapter: Chapter) => void;
  onDeleteChapter: (chapter: Chapter) => void;
  onReorderChapters: (chapters: Chapter[]) => void;
  onDeleteBook: () => void;
  onEditBook?: onEditBookFn;
  isEditingBook?: boolean;
  onAddChapter: () => Promise<Chapter>;
  isDeletingBook: boolean;
  isAddingChapter: boolean;
  isAddingSubchapter?: boolean;
  onAddSubchapter: (chapter: Chapter) => Promise<Subchapter>;
  onReorderSubchapter: (chapterId: number, subchapters: Subchapter[]) => void;
};

export default function BookForm(props: BookFormProps) {
  const { isLimitedEdition } = usePedWorkspaceStatus();
  const {
    book,
    lessonId,
    onUpdateChapter,
    onAddChapter,
    onDeleteBook,
    onEditBook,
    isEditingBook,
    isAddingChapter,
    onReorderChapters,
    onDeleteChapter,
    isDeletingBook,
    onAddSubchapter,
    isAddingSubchapter,
    onReorderSubchapter,
  } = props;

  const { chapters } = book;

  const { isOpen: isOpenModal, open, close } = useToggle();

  const { allowBigChanges } = useCourseEditing();

  const [fullScreen, setFullscreen] = useState(false);

  const [editing, setEditing] = useState(false);

  const { chapter, setChapter } = useChapter({ chapters });

  const { subchapter, setSubchapter } = useSubchapter({ chapter });

  const handleOnAddChapter = async () => {
    const chapter = await onAddChapter();
    setChapter(chapter);
  };

  const handleEditingBook = async () => {
    const result = await onEditBook?.();

    if (result) setEditing(true);
  };

  const handleChapter = (selected: Chapter) => {
    const items = selected.items ?? [];

    const item = items.at(0);

    if (item && item.chapterId !== chapter?.id) {
      setSubchapter(item);
    }

    setChapter(selected);
  };

  const handleSubchapter = (selected: Subchapter) => {
    const findChapter = chapters.find(
      chapter => chapter.id === selected.chapterId,
    );

    if (findChapter && findChapter.id !== chapter?.id) {
      setChapter(findChapter);
    }

    setSubchapter(selected);
  };

  const handleChapterOrSubchapter = (selected: Chapter | Subchapter) => {
    if ('items' in selected) {
      handleChapter(selected);
    }

    if ('sections' in selected) {
      handleSubchapter(selected);
    }
  };

  const readOnly = !editing || !allowBigChanges;

  return (
    <div className="flex flex-col gap-4">
      <AnimatePresence>
        <FullscreenContainer isFullscreen={fullScreen}>
          <BookFormHeader
            isFullscreen={fullScreen}
            editing={editing}
            isEditingBook={isEditingBook}
            allowBigChanges={allowBigChanges && !isLimitedEdition}
            onEditing={setEditing}
            handleEditingBook={handleEditingBook}
            setFullscreen={setFullscreen}
            open={open}
          />
          <BookFormContent
            key={`${chapter?.id}${subchapter?.id}`}
            book={book}
            lessonId={lessonId}
            chapter={chapter}
            subchapter={subchapter}
            editing={editing}
            readOnly={readOnly}
            isAddingChapter={isAddingChapter}
            isAddingSubchapter={isAddingSubchapter}
            handleOnAddChapter={handleOnAddChapter}
            handleChapterOrSubchapter={handleChapterOrSubchapter}
            onUpdateChapter={onUpdateChapter}
            onAddSubchapter={onAddSubchapter}
            onReorderChapters={onReorderChapters}
            onReorderSubchapter={onReorderSubchapter}
            onDeleteChapter={onDeleteChapter}
            isFullscreen={fullScreen}
          />
        </FullscreenContainer>
      </AnimatePresence>
      <ModalDisable
        testId="deleteBookModal"
        modalType="delete"
        onClickConfirm={onDeleteBook}
        visible={isOpenModal}
        onClickCancel={close}
        translationString="modalRemoveBook"
        isRequesting={isDeletingBook}
      />
    </div>
  );
}

type FullscreenContainerProps = PropsWithChildren & {
  isFullscreen: boolean;
};

function FullscreenContainer(props: FullscreenContainerProps) {
  const { isFullscreen, children } = props;

  if (!isFullscreen) return <Fragment>{children}</Fragment>;

  return (
    <motion.div
      className="fixed inset-0 z-20 bg-base-100 p-5 max-h-screen h-full flex flex-col gap-5"
      data-testid="fullscreen-container"
      {...fadeIn}
    >
      {children}
    </motion.div>
  );
}

type BookFormHeaderProps = {
  isFullscreen: boolean;
  editing: boolean;
  isEditingBook?: boolean;
  allowBigChanges: boolean;
  onEditing: (editing: boolean) => void;
  handleEditingBook: () => Promise<void>;
  setFullscreen: (full: boolean) => void;
  open: () => void;
};

type BookFormContentProps = {
  book: Book;
  lessonId: number;
  chapter?: Chapter;
  subchapter?: Subchapter;
  editing: boolean;
  readOnly: boolean;
  isAddingChapter: boolean;
  isAddingSubchapter?: boolean;
  handleOnAddChapter: () => Promise<void>;
  handleChapterOrSubchapter: (selected: Chapter | Subchapter) => void;
  onUpdateChapter: (chapter: Chapter) => void;
  onAddSubchapter: (chapter: Chapter) => Promise<Subchapter>;
  onReorderChapters: (chapters: Chapter[]) => void;
  onReorderSubchapter: (chapterId: number, subchapters: Subchapter[]) => void;
  onDeleteChapter: (chapter: Chapter) => void;
  isFullscreen: boolean;
};

function BookFormHeader({
  isFullscreen,
  editing,
  isEditingBook,
  allowBigChanges,
  onEditing,
  handleEditingBook,
  setFullscreen,
  open,
}: BookFormHeaderProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'book.form',
  });

  const { t: tScreen } = useTranslation('translation', {
    keyPrefix: 'klassView.fullScreen',
  });

  return (
    <div className="flex w-full justify-between items-center flex-wrap gap-2">
      <div className="flex gap-4 items-center">
        <Text
          text={t('title')}
          className={
            isFullscreen
              ? 'text-primary text-24 font-500'
              : 'text-primary text-20 font-500'
          }
        />
      </div>

      <div className="flex gap-4 items-end">
        <MainButton
          className="text-primary font-500 gap-1 flex-row-reverse"
          icon={isFullscreen ? <XMarkIcon /> : <ArrowsPointingOutIcon />}
          color="custom"
          text={isFullscreen ? tScreen('exit') : tScreen('enter')}
          onClick={() => setFullscreen(!isFullscreen)}
        />

        <ConditionalRenderer
          condition={!editing}
          fallback={
            <MainButton
              color="custom"
              text={t('view')}
              onClick={() => onEditing(false)}
              className="text-primary font-500 gap-1 flex-row-reverse"
              icon={<EyeIcon className="text-primary" />}
            />
          }
        >
          <MainButton
            dataTestId="editBookButton"
            color="custom"
            text={t('edit')}
            loading={isEditingBook}
            onClick={handleEditingBook}
            className="text-primary font-500 gap-1 flex-row-reverse"
            icon={<PencilIcon className="text-primary" />}
          />

          <PedWorkspaceGuard isBlocked={!allowBigChanges}>
            <MainButton
              color="custom"
              text={t('delete')}
              disabled={!allowBigChanges}
              onClick={open}
              className="text-error font-500 gap-1 flex-row-reverse"
              icon={<TrashIcon className="text-error" />}
            />
          </PedWorkspaceGuard>
        </ConditionalRenderer>
      </div>
    </div>
  );
}

function BookFormContent({
  book,
  lessonId,
  chapter,
  subchapter,
  editing,
  readOnly,
  isAddingChapter,
  isAddingSubchapter,
  handleOnAddChapter,
  handleChapterOrSubchapter,
  onUpdateChapter,
  onAddSubchapter,
  onReorderChapters,
  onReorderSubchapter,
  onDeleteChapter,
  isFullscreen,
}: BookFormContentProps) {
  const containerClassName = isFullscreen
    ? twMerge(
        'relative w-full h-full flex gap-3 mb-10 pr-2.5',
        scrollBarClassName,
      )
    : 'relative flex gap-3 w-full';

  return (
    <div className={containerClassName}>
      <div className="relative w-full max-w-[calc(100%-268px)]">
        <ConditionalRenderer condition={editing}>
          {chapter && subchapter && (
            <ChapterService
              key={`${chapter?.id}${subchapter?.id}`}
              bookId={book.id}
              bookType={book.bookType}
              lessonId={lessonId}
              chapter={chapter}
              onUpdateChapter={onUpdateChapter}
              subchapter={subchapter}
              isUniqueSubchapter={chapter.items?.length === 1}
            />
          )}
        </ConditionalRenderer>
        <ConditionalRenderer condition={!editing}>
          {chapter && subchapter && (
            <ChapterView chapter={chapter} subchapter={subchapter} />
          )}
        </ConditionalRenderer>
      </div>
      <div className="flex flex-col gap-3.5">
        <Chapters
          chapters={book.chapters}
          activeChapterId={chapter?.id}
          activeSubchapterId={subchapter?.id}
          readOnly={readOnly}
          onReorderChapters={onReorderChapters}
          onAddChapter={handleOnAddChapter}
          isAddingChapter={isAddingChapter}
          isAddingSubchapter={isAddingSubchapter}
          onChapterOrSubchapter={handleChapterOrSubchapter}
          onAddSubchapter={onAddSubchapter}
          onReorderSubchapter={onReorderSubchapter}
          onUpdateChapter={onUpdateChapter}
          lessonId={lessonId}
          bookId={book.id}
          bookType={book.bookType}
          onDeleteChapter={onDeleteChapter}
          isUniqueChapter={book.chapters.length === 1}
        />
        {chapter && subchapter && (
          <Instructions
            lessonId={lessonId}
            bookId={book.id}
            sections={subchapter.sections}
            readOnly={readOnly}
            subchapter={subchapter}
            onSubmit={onUpdateChapter}
            chapter={chapter}
          />
        )}
      </div>
    </div>
  );
}
